import { combineReducers } from '@reduxjs/toolkit';

import {
    ArticlesEtRemisesDesDossiersDeFormalitApi,
    DevisLignesFormalitApi,
    FacturationFormalitApi,
    Configuration as FacturationConfiguration,
    FactureApi,
    ConfigurationParameters as FactureFormaliteConfigurationParameters,
    DevisFormalitApi,
    FactureOpenSearchApi,
    AjustementDePrestationFormalitApi,
    DevisLignesAnnonceLgaleApi,
    PanierPaiementApi,
} from '../../openApi/ApiFacturation';
import { createDevisLigneSlice, DEVIS_LIGNE_SLICE_NAME } from './slices/devisLigneSlice';
import { createDevisSlice, DEVIS_SLICE_NAME } from './slices/devisSlice';
import {
    createFactureFormaliteSlice,
    FACTURE_FORMALITE_SLICE_NAME,
} from './slices/factureFormaliteSlice';
import { buildQuoteModel } from './Models';
import { buildQuoteLineModel } from './Models/quoteLine';
import {
    createFactureOpenSearchSlice,
    FACTURES_OPEN_SEARCH_SLICE_NAME,
} from './slices/factureOpenSearchSlice';

export function createFactureFormaliteReducer(
    configuration: FactureFormaliteConfigurationParameters,
) {
    const { factureFormaliteDataSourcesThunks, factureFormaliteSlice } =
        createFactureFormaliteSlice(configuration);
    return { factureFormaliteDataSourcesThunks, factureFormaliteSlice };
}

export function createDevisReducer(configuration: FactureFormaliteConfigurationParameters) {
    const { devisDataSourcesThunks, devisSlice } = createDevisSlice(configuration);
    return { devisDataSourcesThunks, devisSlice };
}

export function createDevisLigneReducer(configuration: FactureFormaliteConfigurationParameters) {
    const { devisLigneDataSourcesThunks, devisLigneSlice } = createDevisLigneSlice(configuration);
    return { devisLigneDataSourcesThunks, devisLigneSlice };
}

export function createFactureOpenSearchReducer(
    configuration: FactureFormaliteConfigurationParameters,
) {
    const { factureOpenSearchDataSourcesThunks, factureOpenSearchSlice } =
        createFactureOpenSearchSlice(configuration);
    return { factureOpenSearchDataSourcesThunks, factureOpenSearchSlice };
}
export function buildFacturationRedux(configuration: FactureFormaliteConfigurationParameters) {
    const facturationConfiguration = new FacturationConfiguration(configuration);

    const articlesEtRemisesDesDossiersDeFormalitApi = new ArticlesEtRemisesDesDossiersDeFormalitApi(
        facturationConfiguration,
    );
    const devisFormaliteApi = new DevisFormalitApi(facturationConfiguration);
    const devisLignesFormaliteApi = new DevisLignesFormalitApi(facturationConfiguration);
    const facturationFormaliteApi = new FacturationFormalitApi(facturationConfiguration);
    const ajustementDePrestationFormalitApi = new AjustementDePrestationFormalitApi(
        facturationConfiguration,
    );
    const panierPaiementApi = new PanierPaiementApi(facturationConfiguration);
    const factureApi = new FactureApi(facturationConfiguration);
    const facturesOpenSearchApi = new FactureOpenSearchApi(facturationConfiguration);
    const ligneDevisAnnonceLegaleApi = new DevisLignesAnnonceLgaleApi(facturationConfiguration);

    const { factureFormaliteDataSourcesThunks, factureFormaliteSlice } =
        createFactureFormaliteReducer(facturationConfiguration);
    const { devisDataSourcesThunks, devisSlice } = createDevisReducer(facturationConfiguration);
    const { devisLigneDataSourcesThunks, devisLigneSlice } =
        createDevisLigneReducer(facturationConfiguration);
    const { factureOpenSearchDataSourcesThunks, factureOpenSearchSlice } =
        createFactureOpenSearchReducer(facturationConfiguration);

    const combinedFactReducers = combineReducers({
        [FACTURE_FORMALITE_SLICE_NAME]: factureFormaliteSlice.reducer,
        [DEVIS_SLICE_NAME]: devisSlice.reducer,
        [DEVIS_LIGNE_SLICE_NAME]: devisLigneSlice.reducer,
        [FACTURES_OPEN_SEARCH_SLICE_NAME]: factureOpenSearchSlice.reducer,
    });

    return {
        serviceConfigurationParameters: configuration,
        serviceConfiguration: facturationConfiguration,
        reducer: combinedFactReducers,
        apis: {
            articlesEtRemisesDesDossiersDeFormalitApi,
            devisFormaliteApi,
            devisLignesFormaliteApi,
            facturationFormaliteApi,
            ajustementDePrestationFormalitApi,
            panierPaiementApi,
            factureApi,
            facturesOpenSearchApi,
            ligneDevisAnnonceLegaleApi,
        },
        slices: {
            factureFormaliteSlice,
            devisSlice,
            devisLigneSlice,
            factureOpenSearchSlice,
        },
        thunks: {
            factureFormaliteDataSourcesThunks,
            devisDataSourcesThunks,
            devisLigneDataSourcesThunks,
            factureOpenSearchDataSourcesThunks,
        },

        fetchItems: {
            getFacturesFormalite: factureFormaliteDataSourcesThunks.main.getData,
            getDevis: devisDataSourcesThunks.main.getData,
            getDevisLignes: devisLigneDataSourcesThunks.main.getData,
            getFactureOpenSearch: factureOpenSearchDataSourcesThunks.main.getData,
        },

        getItems: {
            selectFacturesFormalite: factureFormaliteDataSourcesThunks.main.getSelected,
            selectDevis: devisDataSourcesThunks.main.getSelected,
            selectDevisLignes: devisLigneDataSourcesThunks.main.getSelected,
            selectFactureOpenSearch: factureOpenSearchDataSourcesThunks.main.getSelected,
        },

        models: {
            quote: buildQuoteModel(devisFormaliteApi, devisDataSourcesThunks),
            quoteLine: buildQuoteLineModel(devisLignesFormaliteApi, devisLigneDataSourcesThunks),
        },
    };
}
